import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItemText,
  Checkbox,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  ListItemButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import type { GroupData } from '../../types/api.types';
import { buildCheckoutUrl } from '../../utils/billing';
import {
  FREE_GROUP_CHANNEL_LIMIT,
  FREE_GROUP_LIMIT,
  PRO_GROUP_CHANNEL_LIMIT,
  getChannelLimit,
} from '../../utils/limits';
import PaywallModal from '../common/PaywallModal';

interface GroupAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  groups: GroupData[];
  // channelId: string;
  onAssign: (opts: { selectedGroupNames: string[]; newGroupName?: string }) => Promise<void>;
  userHasPro: boolean;
  userBillingId: string;
}

export const GroupAssignmentModal: React.FC<GroupAssignmentModalProps> = ({
  open,
  onClose,
  groups,
  // channelId, // reserved for future needs
  onAssign,
  userHasPro,
  userBillingId,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const existingGroupNames = useMemo(() => groups.map(g => g.name), [groups]);
  const filteredGroups = useMemo(
    () => groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase())),
    [groups, search],
  );
  const channelLimit = getChannelLimit(userHasPro);
  const checkoutUrl = buildCheckoutUrl(userBillingId);
  const selectableGroups = useMemo(
    () => filteredGroups.filter(g => g.channelIds.length < channelLimit),
    [filteredGroups, channelLimit],
  );

  const toggle = (name: string) => {
    setSelected(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]));
  };

  const handleSelectAll = () => {
    if (selected.length === selectableGroups.length) {
      setSelected([]);
    } else {
      setSelected(selectableGroups.map(g => g.name));
    }
  };

  const resetAndClose = () => {
    setSelected([]);
    setSearch('');
    setNewGroupName('');
    setShowNewGroup(false);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      const trimmed = newGroupName.trim();
      if (selected.length === 0 && !trimmed) {
        setError('Please select at least one group or create a new group');
        return;
      }
      if (trimmed && existingGroupNames.includes(trimmed)) {
        setError('A group with this name already exists');
        return;
      }
      if (!userHasPro && trimmed && groups.length >= FREE_GROUP_LIMIT) {
        setError(`Free plan allows up to ${FREE_GROUP_LIMIT} groups. Upgrade for unlimited groups.`);
        setPaywallOpen(true);
        return;
      }

      setLoading(true);
      await onAssign({ selectedGroupNames: selected, newGroupName: trimmed || undefined });
      resetAndClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add to groups');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>Add to Groups</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1">Select Groups ({selected.length} selected)</Typography>
            {selectableGroups.length > 0 && (
              <Button size="small" onClick={handleSelectAll} disabled={loading}>
                {selected.length === selectableGroups.length && selectableGroups.length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </Button>
            )}
          </Box>

          {!userHasPro && groups.length >= FREE_GROUP_LIMIT && (
            <Alert severity="warning">Upgrade to create more than {FREE_GROUP_LIMIT} groups.</Alert>
          )}

          {groups.length > 0 && (
            <TextField
              placeholder="Search groups..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              size="small"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
          )}

          <Box
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {groups.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="text.secondary">No groups yet. Create a new group below.</Typography>
              </Box>
            ) : filteredGroups.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="text.secondary">No groups found matching "{search}"</Typography>
              </Box>
            ) : (
              <List dense>
                {filteredGroups
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(g => {
                    const groupHasProLimit = g.channelIds.length >= channelLimit;
                    return (
                      <ListItemButton
                        key={g.name}
                        onClick={() => {
                          if (groupHasProLimit && !selected.includes(g.name)) {
                            setError(
                              userHasPro
                                ? `Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`
                                : `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
                            );
                            if (!userHasPro) {
                              setPaywallOpen(true);
                            }
                            return;
                          }
                          setError(null);
                          toggle(g.name);
                        }}
                        disabled={loading}
                        sx={{ opacity: groupHasProLimit ? 0.6 : 1 }}
                      >
                        <Checkbox edge="start" checked={selected.includes(g.name)} tabIndex={-1} disableRipple />
                        <ListItemText primary={g.name} secondary={`(${g.channelIds.length}) channels`} />
                      </ListItemButton>
                    );
                  })}
              </List>
            )}
          </Box>

          <Divider />

          {!showNewGroup ? (
            <Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  if (!userHasPro && groups.length >= FREE_GROUP_LIMIT) {
                    setError(`Free plan allows up to ${FREE_GROUP_LIMIT} groups. Upgrade for unlimited groups.`);
                    setPaywallOpen(true);
                    return;
                  }
                  setError(null);
                  setShowNewGroup(true);
                }}
                disabled={loading}
                data-cy="create-new-group-toggle"
              >
                Create New Group
              </Button>
            </Box>
          ) : (
            <TextField
              label="New Group Name"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              fullWidth
              placeholder="e.g., Tech, Gaming, Educational"
              disabled={loading}
              inputProps={{ 'data-cy': 'new-group-name-input' }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (selected.length === 0 && !newGroupName.trim())}
          data-cy="submit-add-to-groups"
        >
          {loading ? 'Adding...' : 'Add to Groups'}
        </Button>
      </DialogActions>
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} checkoutUrl={checkoutUrl} />
    </Dialog>
  );
};
